import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    ArivisViewer,
    DenyTrialExtensionPayload,
    ErrorContext,
    ListResponse,
    ListSearch,
    MyPrivacySettings,
    RequestExtensionPayload,
    ResourceUsageResponse,
    TrialExtensionRequest,
    UserDetail,
    UserInfo,
    UserLimit,
    UserLimitKind,
    UserSubscription,
    UserUpdate,
} from '@ca/core/models';
import { convertToMinutes } from '@ca/utils';
import { Observable, map } from 'rxjs';
import { errorContextToken } from 'src/app/core/interceptors/global-error.interceptor';
import { AdminUser } from 'src/app/core/models/admin-user.model';

interface ListResponseWithUserDetailResults {
    results: UserInfo[];
}

@Injectable({
    providedIn: 'root',
})
export class UserApiService {
    protected apiUrl = '/api/identity/api/users';
    protected userServiceApiUrl = '/api/user_service/v1';
    protected appApiUrl = `${this.userServiceApiUrl}/user`;
    private userScopedApiUrl = '/api/identity/users';

    constructor(private http: HttpClient) {}

    getMyProfileInfo$(id: string): Observable<UserInfo> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'MY_WORKSPACE.USER_PROFILE.ERROR_LOADING_BASIC_INFO' }] };
        return this.http.get<UserInfo>(`${this.userScopedApiUrl}/${id}`, { context: new HttpContext().set(errorContextToken, errorContext) });
    }

    getMyPrivacySettings$(): Observable<MyPrivacySettings> {
        return this.http.get<MyPrivacySettings>(`${this.userScopedApiUrl}/privacysettings`);
    }

    getUserSubscription$(): Observable<UserSubscription> {
        const key = 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_LOADING_USER_SUBSCRIPTION';
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [
                { code: 400, key },
                { code: 404, key },
                { code: 500, key },
            ],
        };
        return this.http
            .get<UserSubscription>(`${this.userServiceApiUrl}/subscription`, {
                context: new HttpContext().set(errorContextToken, errorContext),
            })
            .pipe(
                // Uncomment for easier debugging because without this mock DB column for trial end would need to be altered every time
                //map(() => getMockUserSubscription()),
                map((userSubscription: UserSubscription): UserSubscription => {
                    return userSubscription;
                }),
            );
    }

    searchTrialExtensionRequests$(options: ListSearch): Observable<ListResponse<TrialExtensionRequest>> {
        let params = new HttpParams();
        params = params.append('text', options.searchQuery);
        params = params.append('page[number]', options.pageNumber.toString());
        params = params.append('page[size]', options.pageSize.toString());
        if (options.sortBy) {
            params = params.append('sort', options.sortBy);
        }
        if (options.userTypes) {
            params = params.append('userTypes', options.userTypes!);
        }
        return this.http.get<ListResponse<TrialExtensionRequest>>(`${this.userServiceApiUrl}/trialExtensions/search`, {
            params,
        });
    }

    searchUsers$(options: ListSearch): Observable<ListResponse<AdminUser>> {
        let params = new HttpParams();
        params = params.append('searchTerm', options.searchQuery);
        params = params.append('pageNumber', options.pageNumber.toString());
        params = params.append('pageSize', options.pageSize.toString());
        if (options.sortBy) {
            params = params.append('sortBy', options.sortBy);
        }
        return this.http.get<ListResponse<AdminUser>>(`${this.appApiUrl}/search`, {
            params,
        });
    }

    deleteUser$(userId: string): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${userId}`);
    }

    requestTrialExtension$(payload?: RequestExtensionPayload): Observable<void> {
        const key = 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_SENDING_TRIAL_EXTENSION_REQUEST';
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [
                { code: 404, key },
                { code: 500, key },
            ],
        };
        return this.http.post<void>(`${this.userServiceApiUrl}/trialExtensions/requestTrialExtension`, payload, {
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    approveTrialExtension$(trialExtensionId: string): Observable<void> {
        const key = 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_APPROVING_TRIAL_EXTENSION';
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [
                { code: 404, key },
                { code: 500, key },
            ],
        };
        return this.http.patch<void>(`${this.userServiceApiUrl}/trialExtensions/${trialExtensionId}/approve`, {
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    denyTrialExtension$(trialExtensionId: string, payload: DenyTrialExtensionPayload): Observable<void> {
        const key = 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_DENYING_TRIAL_EXTENSION';
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [
                { code: 404, key },
                { code: 500, key },
            ],
        };
        return this.http.patch<void>(`${this.userServiceApiUrl}/trialExtensions/${trialExtensionId}/deny`, payload, {
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    requestSubscriptionOffer$(): Observable<boolean> {
        const key = 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_SENDING_OFFER_REQUEST';
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [
                { code: 404, key },
                { code: 500, key },
            ],
        };
        return this.http.post<boolean>(`${this.userServiceApiUrl}/subscription/requestupgrade`, undefined, {
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    cancelSubscription$(): Observable<boolean> {
        const key = 'MY_WORKSPACE.USER_PROFILE.SUBSCRIPTION.ERROR_CANCELLING_SUBSCRIPTION';
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [
                { code: 404, key },
                { code: 500, key },
            ],
        };
        return this.http.post<boolean>(
            `${this.userServiceApiUrl}/subscription/cancel`,
            {},
            { context: new HttpContext().set(errorContextToken, errorContext) },
        );
    }

    getUserLimits$(): Observable<Map<UserLimitKind, UserLimit | string>> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'MY_WORKSPACE.USER_PROFILE.ERROR_LOADING_COMPUTATIONAL_TIME' }] };
        return this.http
            .get<ResourceUsageResponse>(`${this.userServiceApiUrl}/subscription/limits`, { context: new HttpContext().set(errorContextToken, errorContext) })
            .pipe(
                // Uncomment for easier debugging because without this mock DB column for trial end would need to be altered every time
                //map(() => getMockResourceUsageResponse()),
                map((res: ResourceUsageResponse): Map<UserLimitKind, string | UserLimit> => {
                    const userLimits: Map<UserLimitKind, UserLimit | string> = res.resourceUsageDtoList.reduce(
                        (acc: Map<UserLimitKind, UserLimit | string>, limit: UserLimit): Map<UserLimitKind, string | UserLimit> => {
                            if (limit.limitKind === 'WorkflowExecutionTimeInSeconds') {
                                limit.limit = convertToMinutes(limit.limit);
                                limit.usage = convertToMinutes(limit.usage);
                                return acc.set(limit.limitKind, limit);
                            } else {
                                return acc.set(limit.limitKind, limit);
                            }
                        },
                        new Map([['SubscriptionEndDate', res.endDate]]),
                    );
                    return userLimits;
                }),
            );
    }

    getPermissions$(): Observable<string[]> {
        return this.http.get<string[]>(`${this.appApiUrl}/permissions`);
    }

    updateUserProfile$(payload: UserUpdate): Observable<UserInfo> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'GENERAL.CHANGED_FAILURE' }] };
        return this.http.put<UserInfo>(`${this.userScopedApiUrl}/current`, payload, { context: new HttpContext().set(errorContextToken, errorContext) });
    }

    getUserInfos$(ids: string[]): Observable<UserInfo[]> {
        return this.http.post<ListResponseWithUserDetailResults>(`${this.apiUrl}/byids`, { ids }, { params: new HttpParams() }).pipe(
            map((listResponseWithUserDetailResults: ListResponseWithUserDetailResults): UserInfo[] => {
                return listResponseWithUserDetailResults.results.map(user => Object.assign(new UserInfo(user.id, user.firstName, user.lastName), user));
            }),
        );
    }

    getUsersInfoByNameOrUserName$(query: string): Observable<UserDetail[]> {
        return this.http
            .get<ListResponseWithUserDetailResults>(`${this.apiUrl}/byNameOrUserName?query=${query}`)
            .pipe(map((listResponseWithUserDetailResults: ListResponseWithUserDetailResults): UserInfo[] => listResponseWithUserDetailResults.results));
    }

    getUserInfosByQuery$(query: string): Observable<UserDetail[]> {
        return this.http
            .get<ListResponseWithUserDetailResults>(`${this.apiUrl}/byName?query=${query}`)
            .pipe(map((listResponseWithUserDetailResults: ListResponseWithUserDetailResults): UserInfo[] => listResponseWithUserDetailResults.results));
    }

    getArivisViewer$(userId: string): Observable<ArivisViewer> {
        const errorContext: ErrorContext = {
            isEnabled: true,
            errors: [],
        };
        return this.http.get<ArivisViewer>(`${this.appApiUrl}/Viewer/${userId}`, { context: new HttpContext().set(errorContextToken, errorContext) });
    }
}
